import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, Eye } from "lucide-react";
import { getWatchlist, deleteWatch } from "../lib/api";
import { Card, Loader, Badge } from "./Primitives";
import { POS, NEG } from "../lib/format";

const STATUS = {
  watching: ["WATCHING", "#94a3b8"], entry_hit: ["IN BUY ZONE", "#00e5ff"],
  target_hit: ["TARGET HIT · SELL", "#00e676"], stop_hit: ["STOP HIT · SELL", "#ff3b30"],
  missed: ["MISSED ENTRY", "#ffb300"], invalidated: ["SETUP INVALID", "#64748b"], trail_hit: ["TRAIL STOP HIT · SELL", "#ffb300"],
};

function Row({ w, onDelete }) {
  const cur = w.currency === "INR" ? "₹" : "$";
  const px = w.last_price ?? w.price;
  const fmt = (v) => (v == null ? "—" : `${cur}${Number(v).toLocaleString(undefined, { maximumFractionDigits: v < 1 ? 4 : 2 })}`);
  const span = w.target_price - w.stop_price;
  const pos = px != null && span > 0 ? Math.max(0, Math.min(100, ((px - w.stop_price) / span) * 100)) : 50;
  const entryPos = span > 0 ? ((w.entry_price - w.stop_price) / span) * 100 : 50;
  const [label, color] = STATUS[w.status] || STATUS.watching;
  const id = w.symbol.replace(/[^A-Za-z0-9]/g, "-").toLowerCase();
  return (
    <Card className="p-4" testid={`watch-row-${id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-[#f8fafc] truncate">{w.name}</div>
          <div className="font-mono text-[11px] text-[#64748b] truncate">{w.symbol} · {w.segment} · last {fmt(px)}{w.change_pct != null && ` (${w.change_pct > 0 ? "+" : ""}${w.change_pct}%)`}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge color={color}><span data-testid={`watch-status-${id}`}>{label}</span></Badge>
          <button data-testid={`watch-delete-${id}`} onClick={() => onDelete(w.id)} className="text-[#64748b] hover:text-[#ff3b30] transition-colors"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="relative h-1.5 rounded-full mt-4" style={{ background: `linear-gradient(90deg, ${NEG}55, #2d3c59 ${entryPos}%, ${POS}55)` }}>
        <span className="absolute -top-1 w-0.5 h-3.5 bg-[#f8fafc]/60" style={{ left: `${entryPos}%` }} />
        <span className="absolute -top-1.5 w-3 h-3 -ml-1.5 rounded-full bg-[#00e5ff] border-2 border-[#0b1220]" style={{ left: `${pos}%` }} />
      </div>
      <div className="flex justify-between font-mono text-[11px] mt-2">
        <span style={{ color: NEG }} data-testid={`watch-stop-${id}`}>{w.trail_stop ? <>Trail <span className="text-[#ffb300]">{fmt(w.trail_stop)}</span></> : <>Stop {fmt(w.stop_price)}</>}</span>
        <span className="text-[#f8fafc]">Buy {fmt(w.entry_price)}</span>
        <span style={{ color: POS }}>Sell {fmt(w.target_price)}</span>
      </div>
    </Card>
  );
}

export default function Watchlist() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["watchlist"], queryFn: getWatchlist, refetchInterval: 120000 });
  const remove = async (id) => { await deleteWatch(id); qc.invalidateQueries({ queryKey: ["watchlist"] }); };
  if (isLoading) return <Loader label="Loading watchlist…" />;
  const items = data?.items || [];
  return (
    <div className="space-y-4" data-testid="watchlist">
      <div className="text-xs text-[#64748b]">Prices are checked hourly during market hours (crypto 24/7). You get a phone alert when a stock touches its buy price, target (sell) or stop-loss (sell).</div>
      {items.length === 0 ? (
        <Card className="p-10 text-center" testid="watchlist-empty">
          <Eye className="w-8 h-8 text-[#2d3c59] mx-auto mb-3" />
          <div className="text-sm text-[#94a3b8]">Your watchlist is empty.</div>
          <div className="text-xs text-[#64748b] mt-1">Tap "Watch" on any Market Scan pick.</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{items.map((w) => <Row key={w.id} w={w} onDelete={remove} />)}</div>
      )}
    </div>
  );
}
